import React, { memo, useCallback, useMemo } from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid } from "date-fns";
import { getUserDateFormat, convertToDateFnsFormat } from "../../utils/formatDate";
import FleetMapView from "../dashboard/FleetMapView";
import { reportsApi, getOrganizationId } from "../manageData/reportsApi";
import {
  createAnalyticsDateChunks,
  mergeReportResponses,
} from "../../utils/analyticsDateRange";
import { EMPTY_CHART_ROWS, useChartPeriod, useChartValue } from "../common/useChartPeriod";
import {
  AlertsPanel,
  BarPanel,
  CUSTOM_DATE_OPTION,
  DASHBOARD_DATE_OPTIONS,
  DonutPanel,
} from "../vehicle/DashboardGraph";
import "./StopsDashboard.css";

const HEALTH_COLORS = {
  Healthy: "#16a34a",
  Warning: "#f59e0b",
  Critical: "#ef4444",
  Offline: "#94a3b8",
};

const compactSelectSx = {
  minWidth: 92,
  height: 28,
  fontSize: 10,
  fontWeight: 600,
  color: "#334155",
  backgroundColor: "#ffffff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#dbe3ef",
  },
  "& .MuiSelect-select": {
    py: 0.5,
    px: 1.25,
  },
};

const compactDateFieldSx = {
  width: 120,
  "& .MuiInputBase-root": {
    height: 28,
    fontSize: 10,
    fontWeight: 600,
    color: "#334155",
    backgroundColor: "#ffffff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#dbe3ef",
  },
  "& .MuiInputBase-input": {
    py: 0.5,
    px: 0.75,
  },
};

export const getReportRows = (report) => {
  if (Array.isArray(report)) return report;
  if (Array.isArray(report?.data) && report.data.length) return report.data;
  if (Array.isArray(report?.rows) && report.rows.length) return report.rows;
  if (Array.isArray(report?.halts) && report.halts.length) return report.halts;
  if (Array.isArray(report?.visits) && report.visits.length) return report.visits;
  if (Array.isArray(report?.sections)) {
    return report.sections.flatMap((section) =>
      Array.isArray(section?.data) ? section.data : [],
    );
  }
  if (Array.isArray(report?.devices)) {
    return report.devices.flatMap((device) => {
      const nested =
        device?.data ||
        device?.visits ||
        device?.entryExits ||
        device?.halts ||
        [];
      if (Array.isArray(nested) && nested.length) {
        return nested.map((row) => ({
          ...row,
          vehicleName: row.vehicleName || device.vehicleName,
          stopName: row.stopName || device.stopName || device.name,
          stopId: row.stopId || device.stopId,
        }));
      }
      return [device];
    });
  }
  if (Array.isArray(report?.stops)) {
    return report.stops.flatMap((stop) => {
      const nested = stop?.data || stop?.visits || [];
      if (Array.isArray(nested) && nested.length) {
        return nested.map((row) => ({
          ...row,
          stopName: row.stopName || stop.stopName || stop.name,
          stopId: row.stopId || stop.stopId,
        }));
      }
      return [stop];
    });
  }
  return [];
};

const getStopRoutes = (stop = {}) => {
  if (Array.isArray(stop.routes) && stop.routes.length) return stop.routes;
  if (Array.isArray(stop.assignedRoutes) && stop.assignedRoutes.length) {
    return stop.assignedRoutes;
  }
  return [];
};

const getStopRouteNames = (stop = {}) => {
  if (stop.assignedRouteNames) return String(stop.assignedRouteNames);
  const names = getStopRoutes(stop)
    .map((route) => route.name || route.Name)
    .filter(Boolean);
  return names.length ? names.join(", ") : "No Route";
};

export const stopHasAssignedRoute = (stop = {}, assignedStopIds = null) => {
  if (assignedStopIds instanceof Set) {
    if (assignedStopIds.has(String(stop?.id || ""))) return true;
    if (stop?.stopId && assignedStopIds.has(String(stop.stopId))) return true;
  }
  if (getStopRoutes(stop).length) return true;
  if (stop?.routeId || stop?.assignedRouteId) return true;
  const names = getStopRouteNames(stop);
  return Boolean(names && names !== "No Route");
};

const normalizeStopStatus = (stop = {}) =>
  String(stop.status || stop.Status || "")
    .trim()
    .toUpperCase();

const parseDurationSeconds = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 10000 ? value / 1000 : value;
  }
  const text = String(value).trim();
  const hms = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hms) {
    const first = Number(hms[1]);
    const second = Number(hms[2]);
    const third = Number(hms[3] || 0);
    if (hms[3] != null) return first * 3600 + second * 60 + third;
    return first * 60 + second;
  }
  const hours = Number(text.match(/(\d+(?:\.\d+)?)\s*h/i)?.[1] || 0);
  const mins = Number(text.match(/(\d+(?:\.\d+)?)\s*m/i)?.[1] || 0);
  const secs = Number(text.match(/(\d+(?:\.\d+)?)\s*s/i)?.[1] || 0);
  if (hours || mins || secs) return hours * 3600 + mins * 60 + secs;
  const numeric = Number.parseFloat(text);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  return `${String(minutes).padStart(2, "0")}m ${String(remainder).padStart(2, "0")}s`;
};

const getHaltStopName = (row = {}) =>
  row.stopName ||
  row.stop_name ||
  row.StopName ||
  row.name ||
  row.stop ||
  "";

const getHaltStopId = (row = {}) =>
  row.stopId ||
  row.stop_id ||
  row.StopId ||
  row.stopCode ||
  row.StopID ||
  "";

const getHaltVehicleName = (row = {}) =>
  row.vehicleName ||
  row.vehicle_name ||
  row.VehicleName ||
  row.vehicle ||
  row.deviceName ||
  "";

const getHaltTimestamp = (row = {}) =>
  row.exitTime ||
  row.entryTime ||
  row.departureTime ||
  row.arrivalTime ||
  row.endTime ||
  row.startTime ||
  row.timestamp ||
  row.lastUpdated ||
  row.updatedAt ||
  "";

const parseReportDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getHaltDwellSeconds = (row = {}) => {
  const explicit = parseDurationSeconds(
    row.dwellTime ??
      row.dwell_time ??
      row.haltDuration ??
      row.halt_duration ??
      row.duration ??
      row.timeSpent ??
      row.idleTime ??
      row.visitDuration,
  );
  if (explicit > 0) return explicit;

  const entry = parseReportDate(row.entryTime || row.arrivalTime || row.startTime);
  const exit = parseReportDate(row.exitTime || row.departureTime || row.endTime);
  if (entry && exit && exit > entry) {
    return (exit.getTime() - entry.getTime()) / 1000;
  }
  return 0;
};

const HIGH_DWELL_SECONDS = 8 * 60;
const CRITICAL_DWELL_SECONDS = 15 * 60;

const getHealthBucket = (stop, stats) => {
  const status = normalizeStopStatus(stop);
  if (["OFFLINE", "INACTIVE", "DEAD"].includes(status)) return "Offline";
  if (status === "CRITICAL" || (stats?.avgDwell || 0) >= CRITICAL_DWELL_SECONDS) {
    return "Critical";
  }
  if (status === "WARNING" || (stats?.avgDwell || 0) >= HIGH_DWELL_SECONDS) {
    return "Warning";
  }
  if (status && status !== "ACTIVE") return "Warning";
  return "Healthy";
};

function GaugePanel({ title, percent, extra, className = "", error = "" }) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <section className={`vehicle-dashboard-card stops-dashboard-gauge-card ${className}`.trim()}>
      <div className="vehicle-dashboard-panel-header">
        <h3>{t(title)}</h3>
        {extra ? <div className="vehicle-dashboard-header-extras">{extra}</div> : null}
      </div>
      {error ? (
        <span className="vehicle-dashboard-empty">{t(error)}</span>
      ) : (
        <div className="stops-dashboard-gauge">
          <div
            className="stops-dashboard-gauge-ring"
            style={{
              background: `conic-gradient(#22c55e ${clamped * 3.6}deg, #e2e8f0 0deg)`,
            }}
          >
            <div className="stops-dashboard-gauge-inner">
              <strong>{clamped}%</strong>
              <span>{t("Utilized")}</span>
            </div>
          </div>
          <div className="stops-dashboard-gauge-scale">
            <span>0%</span>
            <span>100%</span>
          </div>
        </div>
      )}
    </section>
  );
}

function DateSelect({
  value,
  onChange,
  customStartDate = "",
  customEndDate = "",
  onCustomRangeChange,
}) {
  const { t } = useTranslation();
  const normalizedValue = String(value || "today")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  const isCustom = normalizedValue === CUSTOM_DATE_OPTION;
  const userDateFormat = getUserDateFormat();
  const dateInputFormat = convertToDateFnsFormat(userDateFormat);
  const parseDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return isValid(d) ? d : null;
    const parsed = new Date(`${d}T00:00:00`);
    return isValid(parsed) ? parsed : null;
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      <Select
        size="small"
        value={normalizedValue}
        onChange={(event) => onChange(event.target.value)}
        sx={compactSelectSx}
      >
        {DASHBOARD_DATE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
            {t(option.label)}
          </MenuItem>
        ))}
      </Select>
      {isCustom && onCustomRangeChange ? (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <DatePicker
              value={parseDate(customStartDate)}
              onChange={(newDate) => {
                const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                onCustomRangeChange(val, customEndDate);
              }}
              maxDate={parseDate(customEndDate) || undefined}
              format={dateInputFormat}
              slotProps={{
                textField: {
                  size: "small",
                  placeholder: userDateFormat,
                  sx: compactDateFieldSx,
                },
              }}
            />
            <Typography component="span" sx={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>
              {t("to")}
            </Typography>
            <DatePicker
              value={parseDate(customEndDate)}
              minDate={parseDate(customStartDate) || undefined}
              onChange={(newDate) => {
                const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                onCustomRangeChange(customStartDate, val);
              }}
              format={dateInputFormat}
              slotProps={{
                textField: {
                  size: "small",
                  placeholder: userDateFormat,
                  sx: compactDateFieldSx,
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      ) : null}
    </Box>
  );
}

export function buildStopVisitStats(haltInput = []) {
  const haltRows = Array.isArray(haltInput) ? haltInput : getReportRows(haltInput);
  const byStop = new Map();

  haltRows.forEach((row) => {
    const name = getHaltStopName(row);
    const code = String(getHaltStopId(row) || "");
    const key = code || name || "unknown";
    if (!byStop.has(key)) {
      byStop.set(key, {
        key,
        name: name || code || "Unknown",
        stopId: code,
        visits: 0,
        dwellTotal: 0,
        lastVehicle: "",
        lastUpdated: "",
      });
    }
    const stats = byStop.get(key);
    stats.visits += 1;
    stats.dwellTotal += getHaltDwellSeconds(row);
    const vehicle = getHaltVehicleName(row);
    const timestamp = getHaltTimestamp(row);
    if (vehicle) stats.lastVehicle = vehicle;
    if (timestamp) stats.lastUpdated = timestamp;
  });

  byStop.forEach((stats) => {
    stats.avgDwell = stats.visits ? stats.dwellTotal / stats.visits : 0;
  });

  return byStop;
}

export function matchStopStats(stop, visitStats) {
  const stopCode = String(stop.stopId || "");
  const stopName = String(stop.name || "");
  if (stopCode && visitStats.has(stopCode)) return visitStats.get(stopCode);
  if (stopName) {
    for (const stats of visitStats.values()) {
      if (stats.name === stopName || stats.stopId === stopCode) return stats;
    }
  }
  return { visits: 0, avgDwell: 0, lastVehicle: "", lastUpdated: "" };
}

export function formatStopDwell(seconds) {
  return formatDuration(seconds);
}

export const fetchStopVisitReport = async (reportParams) => {
  const { dateOption, customStartDate, customEndDate, ...baseParams } = reportParams;
  const customRange = {
    start: customStartDate,
    end: customEndDate,
    startDate: customStartDate,
    endDate: customEndDate,
  };
  const chunks = createAnalyticsDateChunks(dateOption, customRange);

  const fetchCategoryChunks = async (category) => {
    if (!chunks.length) {
      return reportsApi.getEntryExitReport({
        ...reportParams,
        category,
        selectedIds: [],
        selectAll: true,
      });
    }

    if (chunks.length === 1) {
      return reportsApi.getEntryExitReport({
        ...baseParams,
        ...chunks[0],
        category,
        selectedIds: [],
        selectAll: true,
      });
    }

    const chunkResponses = [];
    const BATCH_SIZE = 3;
    for (let i = 0; i < chunks.length; i += BATCH_SIZE) {
      const batch = chunks.slice(i, i + BATCH_SIZE);
      const batchResponses = await Promise.all(
        batch.map((chunk) =>
          reportsApi
            .getEntryExitReport({
              ...baseParams,
              ...chunk,
              category,
              selectedIds: [],
              selectAll: true,
            })
            .catch((err) => {
              if (err?.response?.status >= 400) throw err;
              console.warn(`Failed to fetch ${category} entry-exit chunk:`, err);
              return null;
            }),
        ),
      );
      chunkResponses.push(...batchResponses.filter(Boolean));
    }

    return mergeReportResponses(chunkResponses);
  };

  try {
    const stopReport = await fetchCategoryChunks("stops");
    if (getReportRows(stopReport).length) return stopReport;
  } catch (error) {
    if (error?.response?.data || error?.response?.status) throw error;
    console.error("Failed to load stop entry/exit report by stops:", error);
  }

  try {
    const vehicleReport = await fetchCategoryChunks("vehicles");
    if (getReportRows(vehicleReport).length) return vehicleReport;
  } catch (error) {
    if (error?.response?.data || error?.response?.status) throw error;
    console.error("Failed to load stop entry/exit report by vehicles:", error);
  }

  return null;
};

const toVisitRows = (visitStats) =>
  Array.from(visitStats.values())
    .filter((stats) => stats.visits > 0)
    .sort((a, b) => b.visits - a.visits)
    .slice(0, 5)
    .map((stats) => ({
      label: stats.name,
      value: stats.visits,
      color: "#3b82f6",
    }));

const toDwellRows = (visitStats) =>
  Array.from(visitStats.values())
    .filter((stats) => stats.avgDwell > 0)
    .sort((a, b) => b.avgDwell - a.avgDwell)
    .slice(0, 6)
    .map((stats) => ({
      label: stats.name,
      value: stats.avgDwell,
      color: "#f97316",
    }));

const toUtilization = (stops, visitStats) => {
  let visitedStops = 0;
  stops.forEach((stop) => {
    if (matchStopStats(stop, visitStats).visits > 0) visitedStops += 1;
  });
  const total = stops.length || 1;
  const uniqueVisited = Math.max(
    visitedStops,
    Array.from(visitStats.values()).filter((stats) => stats.visits > 0).length,
  );
  return Math.round((uniqueVisited / total) * 100);
};

const loadStopChartReport = (siteId, period) =>
  fetchStopVisitReport({
    orgId: getOrganizationId(),
    siteId,
    dateOption: period.dateOption,
    customStartDate: period.dateOption === CUSTOM_DATE_OPTION ? period.customStart : undefined,
    customEndDate: period.dateOption === CUSTOM_DATE_OPTION ? period.customEnd : undefined,
  });

function StopsDashboard({
  stopsData = [],
  haltReport,
  dateOption = "today",
  customStartDate = "",
  customEndDate = "",
  siteId,
  error = "",
}) {
  const navigate = useNavigate();
  const visitPeriod = useChartPeriod(dateOption, customStartDate, customEndDate);
  const dwellPeriod = useChartPeriod(dateOption, customStartDate, customEndDate);
  const utilizationPeriod = useChartPeriod(dateOption, customStartDate, customEndDate);
  const loadVisitReport = useCallback(
    () => loadStopChartReport(siteId, visitPeriod),
    [siteId, visitPeriod.dateOption, visitPeriod.customStart, visitPeriod.customEnd],
  );
  const loadDwellReport = useCallback(
    () => loadStopChartReport(siteId, dwellPeriod),
    [siteId, dwellPeriod.dateOption, dwellPeriod.customStart, dwellPeriod.customEnd],
  );
  const loadUtilizationReport = useCallback(
    () => loadStopChartReport(siteId, utilizationPeriod),
    [siteId, utilizationPeriod.dateOption, utilizationPeriod.customStart, utilizationPeriod.customEnd],
  );
  const visitReport = useChartValue(visitPeriod, haltReport, loadVisitReport, null);
  const dwellReport = useChartValue(dwellPeriod, haltReport, loadDwellReport, null);
  const utilizationReport = useChartValue(
    utilizationPeriod,
    haltReport,
    loadUtilizationReport,
    null,
  );

  const stops = useMemo(
    () => (Array.isArray(stopsData) ? stopsData : []),
    [stopsData],
  );
  const haltRows = useMemo(() => getReportRows(haltReport), [haltReport]);
  const visitStats = useMemo(() => buildStopVisitStats(haltRows), [haltRows]);

  const dashboardData = useMemo(() => {
    const healthCounts = { Healthy: 0, Warning: 0, Critical: 0, Offline: 0 };
    let visitedStops = 0;
    let highDwell = 0;
    let noVisit = 0;
    let offline = 0;
    let lowConnectivity = 0;

    stops.forEach((stop) => {
      const stats = matchStopStats(stop, visitStats);
      const bucket = getHealthBucket(stop, stats);
      healthCounts[bucket] += 1;
      if (stats.visits > 0) visitedStops += 1;
      else noVisit += 1;
      if (stats.avgDwell >= HIGH_DWELL_SECONDS) highDwell += 1;
      if (bucket === "Offline") offline += 1;
      if (bucket === "Warning") lowConnectivity += 1;
    });

    const reportVisitRows = Array.from(visitStats.values())
      .filter((stats) => stats.visits > 0)
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 5)
      .map((stats) => ({
        label: stats.name,
        value: stats.visits,
        color: "#3b82f6",
      }));
    const reportDwellRows = Array.from(visitStats.values())
      .filter((stats) => stats.avgDwell > 0)
      .sort((a, b) => b.avgDwell - a.avgDwell)
      .slice(0, 6)
      .map((stats) => ({
        label: stats.name,
        value: stats.avgDwell,
        color: "#f97316",
      }));

    const total = stops.length || 1;
    const healthy = healthCounts.Healthy;
    const uniqueVisited = Math.max(
      visitedStops,
      Array.from(visitStats.values()).filter((stats) => stats.visits > 0).length,
    );
    const utilization = Math.round((uniqueVisited / total) * 100);

    return {
      healthRows: [
        { label: "Healthy", value: healthCounts.Healthy, color: HEALTH_COLORS.Healthy },
        { label: "Warning", value: healthCounts.Warning, color: HEALTH_COLORS.Warning },
        { label: "Critical", value: healthCounts.Critical, color: HEALTH_COLORS.Critical },
        { label: "Offline", value: healthCounts.Offline, color: HEALTH_COLORS.Offline },
      ],
      healthPercent: Math.round((healthy / total) * 100),
      visitRows: reportVisitRows,
      dwellRows: reportDwellRows,
      utilization,
      alertRows: [
        { label: "High Dwell Time", value: highDwell, tone: "danger" },
        { label: "Low Connectivity", value: lowConnectivity, tone: "warning" },
        { label: "No Vehicle Visited", value: noVisit, tone: "warning" },
        { label: "Devices Offline", value: offline, tone: "danger" },
      ],
    };
  }, [stops, visitStats]);

  const visitStatsForChart = useMemo(
    () => buildStopVisitStats(visitReport.value),
    [visitReport.value],
  );
  const dwellStatsForChart = useMemo(
    () => buildStopVisitStats(dwellReport.value),
    [dwellReport.value],
  );
  const utilizationStatsForChart = useMemo(
    () => buildStopVisitStats(utilizationReport.value),
    [utilizationReport.value],
  );
  const visitRows = useMemo(() => toVisitRows(visitStatsForChart), [visitStatsForChart]);
  const dwellRows = useMemo(() => toDwellRows(dwellStatsForChart), [dwellStatsForChart]);
  const utilization = useMemo(
    () => toUtilization(stops, utilizationStatsForChart),
    [stops, utilizationStatsForChart],
  );
  const needsRange = (period) =>
    period.dateOption === CUSTOM_DATE_OPTION && !(period.customStart && period.customEnd);
  const chartMessage = (period, query, fallback) => {
    if (query.loading) return "Loading stop visits…";
    if (needsRange(period)) return "Pick a start and end date";
    const message = period.matchesPage ? error : query.error;
    return message || fallback;
  };
  const dateSelect = (period) => (
    <DateSelect
      value={period.dateOption}
      onChange={period.setPeriod}
      customStartDate={period.customStart}
      customEndDate={period.customEnd}
      onCustomRangeChange={period.setCustomRange}
    />
  );

  return (
    <div className="stops-dashboard-grid">
      <section className="vehicle-dashboard-card vehicle-dashboard-live-card vehicle-dashboard-live-card--map stops-dashboard-map">
        <div className="vehicle-dashboard-map vehicle-dashboard-map--reusable">
          <FleetMapView filterBy="stops" emptyLabel="All Stops" />
        </div>
      </section>

      <DonutPanel
        className="stops-dashboard-span-3"
        title="Stops Health"
        rows={dashboardData.healthRows}
        total={stops.length}
        centerLabel="Health Result"
        centerValue={`${dashboardData.healthPercent}%`}
        action="VIEW REPORT"
        onActionClick={() => navigate("/reports")}
        emptyMessage="No stops added yet"
      />

      <BarPanel
        className="stops-dashboard-span-3"
        title="Top Stops by Visits"
        rows={visitRows}
        valueLabel=""
        extra={dateSelect(visitPeriod)}
        emptyMessage={chartMessage(visitPeriod, visitReport, "No stop visits recorded yet")}
      />

      <BarPanel
        className="stops-dashboard-span-4"
        title="Avg. Dwell Time by Stop"
        rows={dwellRows}
        valueLabel=""
        valueFormatter={formatDuration}
        extra={dateSelect(dwellPeriod)}
        emptyMessage={chartMessage(dwellPeriod, dwellReport, "No dwell time recorded yet")}
      />

      <GaugePanel
        className="stops-dashboard-span-4"
        title="Stops Utilization"
        percent={needsRange(utilizationPeriod) || utilizationReport.loading ? 0 : utilization}
        extra={dateSelect(utilizationPeriod)}
        error={
          utilizationReport.loading ||
          needsRange(utilizationPeriod) ||
          (utilizationPeriod.matchesPage ? error : utilizationReport.error)
            ? chartMessage(utilizationPeriod, utilizationReport, "")
            : ""
        }
      />

      <AlertsPanel
        className="stops-dashboard-span-4"
        title="Alerts Snapshot"
        rows={dashboardData.alertRows}
        valueSuffix="Stops"
        singularSuffix="Stop"
        onViewAll={() => navigate("/alert", { state: { alertTabKey: "stops" } })}
      />
    </div>
  );
}

export { getStopRouteNames, getStopRoutes, normalizeStopStatus };

export default memo(StopsDashboard);
